// src/components/landing/LandingPage.js
import React, { useRef, useState, useEffect } from 'react';
import { motion, useScroll, useMotionValue } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import UploadStampAnimation from './animations/UploadStampAnimation';
import ConvertAnimation from './animations/ConvertAnimation';
import OrganizeAnimation from './animations/OrganizeAnimation';
/**
 * Landing Page Component
 * Stripe-style layout: Text scrolls normally on left, animations update on right
 */
const LandingPage = () => {
  const navigate = useNavigate();
  const scrollContainerRef = useRef(null);

  // Track scroll progress of the scrollable container
  const { scrollYProgress } = useScroll({
    container: scrollContainerRef,
  });

  return (
    <div ref={scrollContainerRef} className="fixed inset-0 overflow-y-auto overflow-x-hidden bg-app-white">
      <div className="bg-app-white">
        {/* Header */}
        <header className="fixed top-0 left-0 right-0 z-50 bg-app-white border-b border-app-gray-300">
          <div className="max-w-7xl mx-auto px-app-3xl py-app-xl flex justify-between items-center">
            <img src="/logo.svg" alt="Stampstack Logo" className="h-12 w-auto" />
            <button
              onClick={() => navigate('/')}
              className="btn-primary"
            >
              Get Started
            </button>
          </div>
        </header>

        {/* Main Content Container */}
        <div className="pt-24 max-w-7xl mx-auto px-app-3xl">
          <div className="grid grid-cols-2 gap-app-4xl">
            
            {/* LEFT COLUMN: Scrolling Text Content */}
            <div className="space-y-[50vh]">
              
              {/* Hero Section */}
              <section className="min-h-screen flex flex-col justify-center py-app-4xl">
                <h1 className="text-6xl font-bold text-app-gray-900 mb-app-2xl">
                  Exhibits court-ready in minutes
                </h1>
                <p className="text-app-xl text-app-gray-600 mb-app-3xl italic">
                  Stamped, indexed, Bates-numbered. Easy.
                </p>
                <button
                  onClick={() => navigate('/')}
                  className="btn-primary w-fit"
                >
                  Try It Free
                </button>
              </section>

              {/* Upload */}
              <Section
                title="Upload"
                description="Drag and drop your exhibits into the system. We'll handle the rest."
              />

              {/* Stamp */}
              <Section
                title="Stamp"
                description="Each document gets stamped automatically with case information. Adjust placement with ease."
              />

              {/* Auto-Convert */}
              <Section
                title="Auto-Convert"
                description="Documents become searchable PDFs instantly. No more manual OCR or file conversion."
              />

              {/* Organize */}
              <Section
                title="Organize"
                description="Reorder, rename, or split into multiple exhibit sets—each with its own index and Bates numbering."
              />

              {/* Collaborate */}
              <Section
                title="Collaborate"
                description="Invite your team to work on cases together. Assign members to specific cases for seamless collaboration."
              />

              {/* Export */}
              <Section
                title="Export"
                description="One click for a merged, court-ready PDF with index table as page 1. Ready to file."
              />

              {/* Final CTA */}
              <section className="min-h-[50vh] flex flex-col justify-center py-app-4xl">
                <h2 className="text-5xl font-bold text-app-gray-900 mb-app-2xl">
                  Ready to streamline your exhibit prep?
                </h2>
                <button
                  onClick={() => navigate('/')}
                  className="btn-primary w-fit"
                >
                  Start Free Trial
                </button>
              </section>
            </div>

            {/* RIGHT COLUMN: Sticky Animation Area */}
            <div className="relative">
              <div className="sticky top-32 h-[70vh] flex items-center justify-center">
                <AnimationContainer scrollProgress={scrollYProgress} />
              </div>
            </div>

          </div>
        </div>

        {/* Footer spacing */}
        <div className="h-32" />
      </div>
    </div>
  );
};

/**
 * Reusable Section Component for text content
 */
const Section = ({ title, description }) => (
  <section className="min-h-screen flex flex-col justify-center py-app-4xl">
    <h2 className="text-5xl font-bold text-app-gray-900 mb-app-xl">
      {title}
    </h2>
    <p className="text-app-xl text-app-gray-600">
      {description}
    </p>
  </section>
);

/**
 * Animation Container - switches between animations based on scroll
 */
const AnimationContainer = ({ scrollProgress }) => {
  return (
    <div className="w-full h-full flex items-center justify-center overflow-visible">
      <AnimationSwitcher scrollProgress={scrollProgress} />
    </div>
  );
};

/**
 * Animation Switcher - determines which animation to show and calculates progress
 * Upload and Stamp sections share the same animation (combined region)
 */
const AnimationSwitcher = ({ scrollProgress }) => {
  const [currentSection, setCurrentSection] = useState(0);
  const sectionProgressValue = useMotionValue(0);
  
  useEffect(() => {
    const unsubscribe = scrollProgress.on('change', (latest) => {
      // We have 7 sections total for animations
      // Upload & Stamp share ONE animation region (sections 1-2)
      // 0-0.14: Hero (0)
      // 0.14-0.42: Upload+Stamp combined (1) - UploadStampAnimation
      // 0.42-0.56: Convert (2)
      // 0.56-0.70: Organize (3)
      // 0.70-0.84: Collaborate (4)
      // 0.84-1.0: Export (5)
      
      let section = 0;
      let progress = 0;
      
      if (latest < 0.14) {
        section = 0;
        progress = latest / 0.14;
      } else if (latest < 0.42) {
        // Upload+Stamp combined - larger region
        section = 1;
        progress = (latest - 0.14) / 0.28;
      } else if (latest < 0.56) {
        section = 2;
        progress = (latest - 0.42) / 0.14;
      } else if (latest < 0.70) {
        section = 3;
        progress = (latest - 0.56) / 0.14;
      } else if (latest < 0.84) {
        section = 4;
        progress = (latest - 0.70) / 0.14;
      } else {
        section = 5;
        progress = (latest - 0.84) / 0.16;
      }
      
      setCurrentSection(section);
      sectionProgressValue.set(progress);
    });
    
    return () => unsubscribe();
  }, [scrollProgress, sectionProgressValue]);
  
  return (
    <motion.div
      key={currentSection}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      className="w-full h-full overflow-visible"
    >
      {currentSection === 0 && (
        <div className="flex items-center justify-center h-full text-app-2xl font-bold text-app-gray-600">
          Hero
        </div>
      )}
      {currentSection === 1 && <UploadStampAnimation scrollProgress={sectionProgressValue} />}
      {currentSection === 2 && <ConvertAnimation scrollProgress={sectionProgressValue} />}
      {currentSection === 3 && <OrganizeAnimation scrollProgress={sectionProgressValue} />}
      {currentSection === 4 && (
        <div className="flex items-center justify-center h-full text-app-2xl font-bold text-app-gray-600">
          Collaborate
        </div>
      )}
      {currentSection === 5 && (
        <div className="flex items-center justify-center h-full text-app-2xl font-bold text-app-gray-600">
          Export
        </div>
      )}
    </motion.div>
  );
};

export default LandingPage;